// client/src/components/command/ExecuteTab.jsx
// Execute cancellation workflow + view results and CSV export.

import React, { useState } from "react";
import * as api from "../../services/api";
import { useJob, useCommandResults, useCommandStats } from "../../hooks/useCommand";
import JobProgress from "./JobProgress";
import CommandStatsCards from "./CommandStatsCards";
import { formatEasternDateTime } from "../../utils/timezone";

const s = {
    actions: {
        display: "flex", gap: "12px", marginBottom: "20px", flexWrap: "wrap",
        alignItems: "center",
    },
    btn: (variant) => ({
        padding: "10px 20px", borderRadius: "8px", border: "none", cursor: "pointer",
        fontWeight: 700, fontSize: "13px", display: "flex", alignItems: "center", gap: "6px",
        background: variant === "danger" ? "linear-gradient(135deg, #ef4444, #dc2626)" :
            variant === "success" ? "linear-gradient(135deg, #10b981, #059669)" :
                variant === "primary" ? "linear-gradient(135deg, #8b5cf6, #6366f1)" :
                    "rgba(255,255,255,0.06)",
        color: variant ? "#fff" : "#94a3b8",
        textDecoration: "none",
    }),
    warning: {
        background: "rgba(245, 158, 11, 0.1)",
        border: "1px solid rgba(245, 158, 11, 0.2)",
        borderRadius: "8px",
        padding: "14px 18px",
        color: "#fbbf24",
        fontSize: "13px",
        lineHeight: "1.5",
        marginBottom: "16px",
    },
    table: {
        width: "100%", borderCollapse: "separate", borderSpacing: 0,
        background: "linear-gradient(135deg, #1a1f2e 0%, #0d1117 100%)",
        borderRadius: "12px", overflow: "hidden",
        border: "1px solid rgba(139, 92, 246, 0.1)",
        marginTop: "20px",
    },
    th: {
        padding: "10px 14px", textAlign: "left", fontSize: "11px", fontWeight: 700,
        textTransform: "uppercase", letterSpacing: "0.5px",
        color: "#64748b", background: "rgba(139, 92, 246, 0.05)",
        borderBottom: "1px solid rgba(139, 92, 246, 0.1)",
    },
    td: {
        padding: "10px 14px", fontSize: "13px", color: "#e2e8f0",
        borderBottom: "1px solid rgba(255,255,255,0.03)",
    },
    badge: (color) => ({
        display: "inline-block", padding: "2px 8px", borderRadius: "4px",
        fontSize: "11px", fontWeight: 600,
        background: color === "red" ? "rgba(239,68,68,0.15)" :
            color === "green" ? "rgba(16,185,129,0.15)" :
                "rgba(139,92,246,0.15)",
        color: color === "red" ? "#ef4444" :
            color === "green" ? "#10b981" : "#8b5cf6",
    }),
    pagination: {
        display: "flex", justifyContent: "center", gap: "8px", marginTop: "16px",
        alignItems: "center",
    },
    pageBtn: {
        padding: "6px 12px", borderRadius: "6px", border: "none", cursor: "pointer",
        fontSize: "12px", fontWeight: 500,
        background: "rgba(255,255,255,0.06)", color: "#94a3b8",
    },
    filterRow: {
        display: "flex", gap: "8px", marginTop: "16px", marginBottom: "16px",
    },
    filterBtn: (active) => ({
        padding: "6px 14px", borderRadius: "6px", border: "none", cursor: "pointer",
        fontSize: "12px", fontWeight: active ? 700 : 500,
        background: active ? "#8b5cf6" : "rgba(255,255,255,0.06)",
        color: active ? "#fff" : "#94a3b8",
    }),
    error: {
        background: "rgba(239, 68, 68, 0.1)",
        borderRadius: "8px",
        padding: "10px 16px",
        color: "#ef4444",
        fontSize: "13px",
        marginBottom: "12px",
    },
    noData: { padding: "40px", textAlign: "center", color: "#64748b", fontSize: "14px" },
};

export default function ExecuteTab() {
    const [jobId, setJobId] = useState(null);
    const [error, setError] = useState(null);
    const [executingOrderId, setExecutingOrderId] = useState(null);
    const { job, sseConnected } = useJob(jobId);
    const { stats } = useCommandStats(3000);
    const { results, total, loading: resultsLoading, page, filter, setPage, setFilter, reload } = useCommandResults();

    const handleExecute = async () => {
        if (!window.confirm("This will cancel mismatched line items on flagged orders in ShipHero. Items at the correct location will be kept. Continue?")) return;
        setError(null);
        try {
            const res = await api.startExecuteJob();
            setJobId(res.jobId);
        } catch (e) { setError(e.message); }
    };

    const handleExecuteSingle = async (orderId, orderNumber) => {
        if (!window.confirm(`Execute cancellation for order #${orderNumber}? This will cancel mismatched line items for this order only.`)) return;
        setError(null);
        setExecutingOrderId(orderId);
        try {
            const res = await api.startExecuteJob([orderId]);
            setJobId(res.jobId);
            // Poll for completion then refresh
            setTimeout(() => { reload(); setExecutingOrderId(null); }, 3000);
        } catch (e) {
            setError(e.message);
            setExecutingOrderId(null);
        }
    };

    const handleAbort = async () => {
        try { await api.abortCommandJob(jobId); } catch (_) { }
    };

    const [exporting, setExporting] = useState(false);

    const handleCsvExport = async () => {
        setExporting(true);
        try {
            await api.downloadCommandCsv();
        } catch (err) {
            console.error("[Export] Failed:", err);
            alert("Export failed: " + (err.message || "Unknown error"));
        } finally {
            setExporting(false);
        }
    };

    return (
        <div>
            <CommandStatsCards stats={stats} />

            <div style={s.warning}>
                <strong>Execution will cancel only mismatched line items</strong> (not entire orders) and apply tags in ShipHero.
                Line items at the correct location will be kept. This action cannot be undone.
                Review the per-item details in the Review tab before proceeding.
            </div>

            {error && <div style={s.error}>⚠️ {error}</div>}

            <div style={s.actions}>
                <button style={s.btn("danger")} onClick={handleExecute} disabled={job?.status === "running" || (stats?.pending_cancel || 0) === 0}>
                    ⚡ Execute Cancellations ({stats?.pending_cancel || 0} orders)
                </button>
                {job?.status === "running" && (
                    <button style={s.btn("default")} onClick={handleAbort}>⏸️ Pause</button>
                )}
                <button style={s.btn("success")} onClick={handleCsvExport} disabled={exporting}>
                    {exporting ? "⏳ Exporting..." : "📄 Export CSV"}
                </button>
                <button style={s.btn("default")} onClick={reload}>🔄 Refresh Results</button>
            </div>

            {job && <JobProgress job={job} sseConnected={sseConnected} type="execute" />}

            {/* Results Table */}
            <div style={s.filterRow}>
                {["all", "success", "failed"].map(f => (
                    <button key={f} style={s.filterBtn(filter === f)} onClick={() => { setFilter(f); setPage(1); }}>
                        {f === "all" ? "All" : f === "success" ? "✓ Success" : "✗ Failed"}
                    </button>
                ))}
            </div>

            {resultsLoading && results.length === 0 ? (
                <div style={s.noData}>Loading results…</div>
            ) : results.length === 0 ? (
                <div style={s.noData}>No execution results yet.</div>
            ) : (
                <>
                    <table style={s.table}>
                        <thead>
                            <tr>
                                <th style={s.th}>Order #</th>
                                <th style={s.th}>Cancel Status</th>
                                <th style={s.th}>Tag Status</th>
                                <th style={s.th}>Error</th>
                                <th style={s.th}>Processed At</th>
                                <th style={{ ...s.th, textAlign: "center" }}>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {results.map(o => {
                                const canExecute = o.cancel_status !== "success";
                                const isExecuting = executingOrderId === o.id;
                                return (
                                    <tr key={o.id}>
                                        <td style={s.td}><strong>{o.order_number || "—"}</strong></td>
                                        <td style={s.td}>
                                            <span style={s.badge(o.cancel_status === "success" ? "green" : o.cancel_status === "failed" ? "red" : "purple")}>
                                                {o.cancel_status || "—"}
                                            </span>
                                        </td>
                                        <td style={s.td}>
                                            <span style={s.badge(o.tag_status === "success" ? "green" : o.tag_status === "failed" ? "red" : "purple")}>
                                                {o.tag_status || "—"}
                                            </span>
                                        </td>
                                        <td style={{ ...s.td, fontSize: "11px", color: "#ef4444", maxWidth: "200px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={o.execution_error || ""}>
                                            {o.execution_error || "—"}
                                        </td>
                                        <td style={{ ...s.td, fontSize: "11px", color: "#94a3b8" }}>
                                            {o.execution_processed_at ? formatEasternDateTime(o.execution_processed_at) : "—"}
                                        </td>
                                        <td style={{ ...s.td, textAlign: "center" }}>
                                            {canExecute ? (
                                                <button
                                                    onClick={() => handleExecuteSingle(o.id, o.order_number)}
                                                    disabled={isExecuting || job?.status === "running"}
                                                    style={{
                                                        padding: "4px 12px",
                                                        borderRadius: "6px",
                                                        border: "none",
                                                        cursor: isExecuting || job?.status === "running" ? "not-allowed" : "pointer",
                                                        fontSize: "11px",
                                                        fontWeight: 700,
                                                        background: isExecuting ? "rgba(245,158,11,0.2)" : "rgba(239,68,68,0.15)",
                                                        color: isExecuting ? "#fbbf24" : "#ef4444",
                                                        transition: "all 0.15s",
                                                    }}
                                                    title={`Execute cancellation for order #${o.order_number}`}
                                                >
                                                    {isExecuting ? "Running..." : "Execute"}
                                                </button>
                                            ) : (
                                                <span style={{ fontSize: "11px", color: "#10b981" }}>Done</span>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>

                    <div style={s.pagination}>
                        <button style={s.pageBtn} onClick={() => setPage(Math.max(1, page - 1))} disabled={page <= 1}>← Prev</button>
                        <span style={{ color: "#94a3b8", fontSize: "12px" }}>{total.toLocaleString()} results</span>
                        <button style={s.pageBtn} onClick={() => setPage(page + 1)} disabled={results.length < 100}>Next →</button>
                    </div>
                </>
            )}
        </div>
    );
}
